<template>
  <div>
    <div class="title-option">การตั้งค่าสำหรับทุกตัวเลือกสินค้า</div>
    <div v-if="useSummary" class="summary-table">
      <div class="summary-header">
        <div>ตัวเลือก</div>
        <div>ครั้งที่</div>
        <div class="summary-header-cost">
          <div class="summary-header-cost-main">
            <span class="summary-header-cost-title">ราคาทุน (รวม VAT)</span>
            <service-promotion-badge
              v-if="hasPromotionRows"
            ></service-promotion-badge>
          </div>
          <div
            v-if="summaryPromotion.hasPromotionPeriodMismatch"
            class="promotion-warning"
          >
            <span class="promotion-warning-mark">!</span>
            <span>ช่วง Promotion ไม่ตรงกัน กรุณาตรวจสอบ Product Name</span>
          </div>
          <div
            v-else-if="summaryPromotion.promotionPeriod"
            class="promotion-status-line"
          >
            <span class="promotion-period-text">
              {{ summaryPromotion.promotionPeriod }}
            </span>
          </div>
        </div>
      </div>
      <div
        v-for="row in summaryRows"
        :key="row.key"
        class="summary-row"
        :class="{ 'summary-row-promotion': row.hasPromotion }"
      >
        <div class="option-name-cell">
          <span
            class="option-title"
            :class="{ 'option-title-extra': row.isExtraOption }"
          >
            {{ getOptionTitle(row) }}
          </span>
        </div>
        <div class="appointment-cell">ครั้งที่ {{ row.appointNo }}</div>
        <div class="cost-cell">
          <div class="cost-stack">
            <el-input
              class="cost-input"
              type="text"
              inputmode="decimal"
              :disabled="hasOptionSwitch || row.readOnly"
              :value="getCostInputValue(row)"
              @input="
                handleCombineAppointmentCost(
                  row.serviceOptionCostIndex,
                  row.combinationCostIndex,
                  row.appointmentCostIndex,
                  $event
                )
              "
            ></el-input>
            <service-promotion-meta
              v-if="row.hasPromotion || shouldShowRowPromotionPeriod(row)"
              :original-cost="row.originalCost"
              :promotion-period="row.promotionPeriod"
              :show-original-cost="row.hasPromotion"
              :show-promotion-period="shouldShowRowPromotionPeriod(row)"
            ></service-promotion-meta>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Input } from "element-ui";
import {
  formatServicePriceInputValue,
  formatServicePriceDisplay,
  sanitizeServicePriceInput,
} from "../utils/servicePrice";
import ServicePromotionBadge from "./ServicePromotionBadge";
import ServicePromotionMeta from "./ServicePromotionMeta";
import {
  getServiceOptionCostSummaryPromotion,
  getServiceOptionCostSummaryRows,
  shouldUseOptionCostSummary,
} from "../utils/serviceForm";

export default {
  components: {
    "el-input": Input,
    ServicePromotionBadge,
    ServicePromotionMeta,
  },
  props: ["value"],
  computed: {
    serviceOptionCosts: {
      get() {
        return this.value.serviceOptionCosts;
      },
    },
    summaryRows() {
      return getServiceOptionCostSummaryRows(this.serviceOptionCosts, {
        serviceOptions: this.value.serviceOptions,
        serviceProducts: this.value.serviceProducts,
      });
    },
    summaryPromotion() {
      return getServiceOptionCostSummaryPromotion(this.summaryRows);
    },
    hasPromotionRows() {
      return this.summaryPromotion.hasPromotion;
    },
    useSummary() {
      return shouldUseOptionCostSummary(this.serviceOptionCosts);
    },
    hasOptionSwitch() {
      return this.value.hasOptionSwitch;
    }
  },
  methods: {
    getOptionTitle(row) {
      return row.isExtraOption ? `+ ${row.optionTitle}` : row.optionTitle;
    },
    isCostInputReadOnly(row) {
      return this.hasOptionSwitch || row.readOnly;
    },
    getCostInputValue(row) {
      return this.isCostInputReadOnly(row)
        ? this.formatPriceDisplay(row.cost)
        : formatServicePriceInputValue(row.cost, false);
    },
    formatPriceDisplay(cost) {
      return formatServicePriceDisplay(cost);
    },
    shouldShowRowPromotionPeriod(row) {
      return this.summaryPromotion.hasPromotionPeriodMismatch &&
        row.promotionPeriod;
    },
    handleCombineAppointmentCost(
      serviceOptionCostIndex,
      combinationCostIndex,
      appointmentCostIndex,
      cost
    ) {
      const sanitizedCost = sanitizeServicePriceInput(cost);
      const serviceOptionCosts = this.value.serviceOptionCosts.map(
        (serviceOptionCost, index) => {
          if (serviceOptionCostIndex !== index) {
            return serviceOptionCost;
          }

          return {
            ...serviceOptionCost,
            combination_costs: serviceOptionCost.combination_costs.map(
              (combinationCost, combineOptionIndex) => {
                if (combinationCostIndex !== combineOptionIndex) {
                  return combinationCost;
                }

                return {
                  ...combinationCost,
                  appointment_costs: combinationCost.appointment_costs.map(
                    (appointCost, currentAppointmentCostIndex) =>
                      appointmentCostIndex === currentAppointmentCostIndex
                        ? { ...appointCost, cost: sanitizedCost }
                        : appointCost
                  ),
                };
              }
            ),
          };
        }
      );

      this.$emit("input", serviceOptionCosts);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.title-option {
  margin-top: 20px;
  margin-bottom: 4px;
}

.summary-table {
  margin-bottom: 8px;
  overflow: hidden;
  border: $border-head;
  border-radius: 6px;
  background: #fff;
}

.summary-header,
.summary-row {
  display: grid;
  grid-template-columns: minmax(40ch, 2fr) minmax(76px, 0.45fr) minmax(240px, 1.35fr);
  column-gap: 16px;
  align-items: center;
}

.summary-header {
  min-height: 54px;
  padding: 12px 18px;
  background: #f2f2f2;
  border-bottom: $border-head;
  color: #4a4a4a;
  font-size: 15px;
  font-weight: bold;
}

.summary-row {
  min-height: 64px;
  padding: 10px 18px;
  border-bottom: $border;
  color: #343a40;
  font-size: 14px;
  transition: background-color 0.15s ease;
}

.summary-row-promotion {
  background: #fffaf2;
}

.summary-row:last-child {
  border-bottom: 0;
}

.option-name-cell,
.appointment-cell,
.cost-cell {
  min-width: 0;
}

.option-name-cell {
  display: flex;
  align-items: center;
}

.option-title {
  display: inline-block;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;
  font-weight: bold;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-title-extra {
  padding-left: 16px;
  font-weight: normal;
}

.appointment-cell {
  font-weight: bold;
}

.summary-header-cost {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  line-height: 1.2;
}

.summary-header-cost-main {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  flex-wrap: wrap;
}

.summary-header-cost-title {
  color: #4a4a4a;
  font-size: 15px;
  font-weight: bold;
}

.promotion-status-line {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  color: #8a5a13;
  font-size: 12px;
  font-weight: normal;
  line-height: 1.2;
  flex-wrap: wrap;
}

.promotion-period-text {
  color: #8a5a13;
  font-size: 12px;
  font-weight: normal;
  white-space: nowrap;
}

.promotion-warning {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  max-width: 100%;
  color: #a24f00;
  font-size: 12px;
  font-weight: normal;
  line-height: 1.35;
}

.promotion-warning-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  margin-top: 1px;
  border: 1px solid #d87a00;
  border-radius: 50%;
  color: #a24f00;
  font-size: 10px;
  font-weight: bold;
}

.cost-stack {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  max-width: 100%;
}

.cost-input {
  width: 180px;
  max-width: 100%;
}

.summary-row-promotion .cost-input ::v-deep .el-input__inner {
  border-color: #f0b562;
  box-shadow: 0 0 0 1px rgba(240, 181, 98, 0.12);
}

@media (max-width: 767px) {
  .summary-header {
    display: none;
  }

  .summary-row {
    grid-template-columns: 1fr;
    gap: 6px;
    align-items: start;
  }

  .appointment-cell {
    padding-left: 0;
  }
}
</style>
